import type { CommencementBundle } from "~/composables/useCommencement";
import { stageAtLeast } from "~/utils/commencementStages";

export type MemberOnboardingPhase =
  | "awaiting_commencement"
  | "blocked"
  | "brief"
  | "complete"
  | "pending_start"
  | "portal_ready"
  | "proposal"
  | "waiting_spec";

export function resolveMemberOnboardingPhase(
  bundle: CommencementBundle | null,
): MemberOnboardingPhase {
  const project = bundle?.project;
  if (!project) {
    // Enquiry-seeded (or manually started) draft: treat as ready to complete.
    if (bundle?.brief?.has_content && bundle.brief.status === "draft") {
      return "brief";
    }
    return "portal_ready";
  }

  const stage = project.stage;
  if (stage === "commenced") return "complete";

  if (
    bundle.agreement?.signed_at ||
    stageAtLeast(stage, "agreement_signed")
  ) {
    if (bundle.tech_setup?.complete) {
      return "awaiting_commencement";
    }
    return "pending_start";
  }

  if (
    bundle.spec?.status === "published" ||
    project.spec_published_at
  ) {
    return "proposal";
  }

  if (stageAtLeast(stage, "brief_submitted")) {
    return "waiting_spec";
  }

  if (stageAtLeast(stage, "invited")) {
    return "brief";
  }

  return "blocked";
}

export function memberNeedsProjectBriefStart(
  phase: MemberOnboardingPhase,
): boolean {
  return phase === "portal_ready" || phase === "blocked" || phase === "brief";
}

/** Member may open the brief wizard (first time or edits before proposal). */
export function memberCanEditProjectBrief(
  phase: MemberOnboardingPhase,
): boolean {
  return (
    phase === "portal_ready" ||
    phase === "brief" ||
    phase === "waiting_spec"
  );
}

export type MemberSetupCta = {
  label: string;
  show: boolean;
  to: string;
};

export type MemberStatusTone = "action" | "passive" | "success";

export type MemberStatusContent = {
  body: string;
  headline: string;
  meta?: string;
  primaryCta: MemberSetupCta | null;
  secondaryCta?: { label: string; to: string };
  tone: MemberStatusTone;
};

function formatMemberStatusDate(iso: string | null | undefined) {
  if (!iso) return "recently";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function resolveMemberStatusContent(
  phase: MemberOnboardingPhase,
  options: {
    briefHasContent?: boolean;
    briefSubmittedAt?: string | null;
    hasProject: boolean;
    isCommenced: boolean;
    openClarificationsCount?: number;
    projectId?: string | null;
    techSetupAvailable?: boolean;
    techSetupComplete?: boolean;
    techSetupSubmitted?: boolean;
  },
): MemberStatusContent {
  const techSetupTo = options.projectId
    ? `/projects/${options.projectId}/tech-setup`
    : "/tech-setup";
  const projectsTo = options.projectId
    ? `/projects/${options.projectId}`
    : "/projects";

  if (options.isCommenced) {
    return {
      body: "Your project is live. Use the dashboard for day-to-day work.",
      headline: "Project live",
      primaryCta: { label: "Go to dashboard", show: true, to: "/dashboard" },
      secondaryCta: { label: "View project", to: projectsTo },
      tone: "success",
    };
  }

  if (!options.hasProject) {
    if (phase === "brief" || options.briefHasContent) {
      return {
        body: "We've started a project brief from your enquiry. Review the draft, add anything missing, and submit when you're ready.",
        headline: "Complete your project brief",
        primaryCta: {
          label: "Open project brief",
          show: true,
          to: "/project-brief",
        },
        tone: "action",
      };
    }
    return {
      body: "Your portal access is active. Start your project brief whenever you're ready so we can prepare a proposal.",
      headline: "Start your project brief",
      primaryCta: {
        label: "Start project brief",
        show: true,
        to: "/project-brief",
      },
      tone: "action",
    };
  }

  switch (phase) {
    case "blocked":
      return {
        body: "Your portal access is active. Bonapps will connect your project shortly. You'll receive an email when your brief is ready to complete.",
        headline: "We're preparing your project",
        primaryCta: null,
        secondaryCta: { label: "Go to dashboard", to: "/dashboard" },
        tone: "passive",
      };
    case "portal_ready":
      return {
        body: "Your portal access is active. Start your project brief whenever you're ready so we can prepare a proposal.",
        headline: "Start your project brief",
        primaryCta: {
          label: "Start project brief",
          show: true,
          to: "/project-brief",
        },
        tone: "action",
      };
    case "brief":
      return {
        body: options.briefHasContent
          ? "We've started a draft from your enquiry. Review it, add any missing details, and submit when you're ready."
          : "Share your goals, scope, and constraints so we can shape an accurate proposal.",
        headline: "Complete your project brief",
        primaryCta: resolveMemberSetupCta(phase, options),
        tone: "action",
      };
    case "waiting_spec":
      if ((options.openClarificationsCount ?? 0) > 0) {
        const count = options.openClarificationsCount ?? 0;
        return {
          body: `Bonapps need ${count} answer${count === 1 ? "" : "s"} before we can finish your proposal. Reply in the portal and we'll continue preparing your scope and budget.`,
          headline: "Action needed",
          meta: `Brief submitted ${formatMemberStatusDate(options.briefSubmittedAt)}`,
          primaryCta: {
            label: "Answer questions",
            show: true,
            to: "/project-brief",
          },
          secondaryCta: { label: "Update project brief", to: "/project-brief" },
          tone: "action",
        };
      }
      return {
        body: "Thanks for submitting your brief. We typically need a few business days to prepare your scope, budget, and timeline. We'll email you when it's ready to review.",
        headline: "Proposal in progress",
        meta: `Brief submitted ${formatMemberStatusDate(options.briefSubmittedAt)}`,
        primaryCta: {
          label: "View project briefs",
          show: true,
          to: "/project-briefs",
        },
        secondaryCta: { label: "Update project brief", to: "/project-brief" },
        tone: "passive",
      };
    case "proposal":
      return {
        body: "Your scope, budget, and timeline are ready. Review the proposal and sign when you're happy to proceed.",
        headline: "Review your proposal",
        primaryCta: resolveMemberSetupCta(phase, options),
        tone: "action",
      };
    case "pending_start":
      if (!options.techSetupAvailable) {
        return {
          body: "You've approved the proposal. Bonapps is preparing the services you'll need for tech setup and will email you when it's ready.",
          headline: "Preparing tech setup",
          primaryCta: null,
          secondaryCta: { label: "View project", to: projectsTo },
          tone: "passive",
        };
      }
      if (options.techSetupComplete) {
        return {
          body: "Thanks - we've received your tech setup details. Bonapps will connect your services and email you when your project is ready to start.",
          headline: "Tech setup received",
          primaryCta: null,
          secondaryCta: { label: "View tech setup", to: techSetupTo },
          tone: "passive",
        };
      }
      if (options.techSetupSubmitted) {
        return {
          body: "We've saved your progress. Finish any remaining services and confirm each one when setup is complete.",
          headline: "Tech setup in progress",
          primaryCta: {
            label: "Continue tech setup",
            show: true,
            to: techSetupTo,
          },
          tone: "action",
        };
      }
      return {
        body: "Create the required accounts, then add your login details securely so Bonapps can connect your project.",
        headline: "Tech setup",
        primaryCta: {
          label: "Open tech setup",
          show: true,
          to: techSetupTo,
        },
        tone: "action",
      };
    case "awaiting_commencement":
      return {
        body: "Thanks. We've received your tech setup details. Bonapps will connect your services and email you when your project is ready to start.",
        headline: "Waiting to start build",
        meta: "We'll email you when your project goes live.",
        primaryCta: null,
        secondaryCta: { label: "View tech setup", to: techSetupTo },
        tone: "passive",
      };
    default:
      return {
        body: "Continue setting up your project.",
        headline: "Continue setup",
        primaryCta: resolveMemberSetupCta(phase, options),
        tone: "action",
      };
  }
}

export function resolveMemberSetupCta(
  phase: MemberOnboardingPhase,
  options: {
    hasProject: boolean;
    isCommenced: boolean;
    projectId?: string | null;
    techSetupAvailable?: boolean;
  },
): MemberSetupCta {
  const techSetupTo = options.projectId
    ? `/projects/${options.projectId}/tech-setup`
    : "/tech-setup";
  const projectsTo = options.projectId
    ? `/projects/${options.projectId}`
    : "/projects";

  if (options.isCommenced) {
    return { label: "View project", show: true, to: projectsTo };
  }

  if (!options.hasProject) {
    if (memberNeedsProjectBriefStart(phase)) {
      return {
        label:
          phase === "brief" ? "Complete project brief" : "Start project brief",
        show: true,
        to: "/project-brief",
      };
    }
    return { label: "Go to dashboard", show: true, to: "/dashboard" };
  }

  if (memberNeedsProjectBriefStart(phase)) {
    return {
      label: "Complete project brief",
      show: true,
      to: "/project-brief",
    };
  }

  if (phase === "waiting_spec") {
    return {
      label: "Project briefs",
      show: true,
      to: "/project-briefs",
    };
  }

  if (phase === "proposal") {
    return { label: "Review proposal", show: true, to: "/proposal" };
  }

  if (phase === "pending_start") {
    if (!options.techSetupAvailable) {
      return { label: "View project", show: true, to: projectsTo };
    }
    return {
      label: "Open tech setup",
      show: true,
      to: techSetupTo,
    };
  }

  if (phase === "awaiting_commencement") {
    return { label: "View tech setup", show: true, to: techSetupTo };
  }

  return { label: "Continue setup", show: true, to: "/get-started" };
}

export const MEMBER_ONBOARDING_PHASE_LABELS: Record<
  MemberOnboardingPhase,
  string
> = {
  portal_ready: "Portal access active",
  blocked: "Project link pending",
  brief: "Your project brief",
  waiting_spec: "Proposal in progress",
  proposal: "Review & approve",
  pending_start: "Tech setup",
  awaiting_commencement: "Waiting to start",
  complete: "Project live",
};
